import {
  type SessionCheckpointView,
  type TrainingBudgetUnit,
  type TrainingEvalMetricsView,
  type TrainingEvalRunSummaryView,
  type TrainingMetricPointView,
  type TrainingSessionRecipeView,
} from "@/api";
import { parseCheckpointTag, type CheckpointTag } from "@/modules/training/public";
import { runDurationSeconds } from "@/modules/evaluation/public";

// 분석 및 평가·체크포인트 표면의 순수 파생 로직. 축 단위 판정과 체크포인트↔지표·평가 join 을
// 컴포넌트 밖에서 소유한다. 그래서 step 세션에 "에폭" 라벨이 잘못 붙는 회귀를 단위 테스트로 막는다.

// 에폭 단위로 기록하는 지표(validation_* 계열·loss_status·epoch_time). 이 지표들의 MLflow step
// 필드는 epoch 예산 세션에서만 에폭 번호이고, step 예산 세션에서는 전역 스텝이다.
const perEpochMetricPattern = /^validation_|^loss_status$|^epoch_time$/;

// 세션의 budget unit 판정 순서: 제출 overrides → 실제 산출된 체크포인트 태그 접두사 → 평가 요약의
// budget_unit → 기본 epoch. 태그는 트레이너가 실제 단위로 쓴 산출물이다. 그래서 overrides 다음으로
// 가장 직접적인 증거다. 이 함수는 세션의 여러 증거를 합친다.
export function resolveSessionBudgetUnit(
  recipe: TrainingSessionRecipeView | null | undefined,
  policies: readonly SessionCheckpointView[],
  summaries: readonly TrainingEvalRunSummaryView[],
): TrainingBudgetUnit {
  const override = recipe?.config.overrides?.["train.budget.unit"];
  if (override === "step" || override === "epoch") {
    return override;
  }
  for (const checkpoint of policies) {
    const parsed = parseCheckpointTag(checkpoint.epoch);
    if (parsed !== null) {
      return parsed.unit;
    }
  }
  // 요약이 없으면 이 순서의 기본값 epoch 로 끝낸다.
  return summaries[0]?.budget_unit ?? "epoch";
}

// 지표의 X축 단위. MLflow step 필드는 항상 "스텝"이다. 에폭 단위 지표를 epoch 예산 세션에서
// 기록할 때만 에폭 번호가 된다. 그 조합에서만 "에폭"으로 라벨한다.
export function metricAxisUnit(
  metricKey: string,
  budgetUnit: TrainingBudgetUnit,
): TrainingBudgetUnit {
  return perEpochMetricPattern.test(metricKey) && budgetUnit === "epoch" ? "epoch" : "step";
}

export type CheckpointLossJoin = {
  // 저장 시점(또는 그 이하의 가장 가까운 점)의 validation_loss. 히스토리가 없거나 태그를 파싱할 수 없으면 null 이다.
  value: number | null;
  // 태그 번호와 정확히 일치하지 않는 가장 가까운 점을 썼는지(트레이너의 0-index 기록은 1 차이를 허용한다).
  approximate: boolean;
};

export type CheckpointAnalysisRow = {
  checkpoint: SessionCheckpointView;
  tag: CheckpointTag | null;
  loss: CheckpointLossJoin;
  isBest: boolean;
  evalRuns: TrainingEvalRunSummaryView[];
};

// 체크포인트 태그 번호에 대응하는 validation_loss 점을 찾는다. 정확히 일치하는 점을 먼저 찾고, 없으면
// 그 이하의 가장 가까운 점을 쓴다. validation_loss 의 step 필드가 0-index 에폭일 수 있어 `값-1` 도 정확한 일치로 본다.
function joinLossAtTag(
  tag: CheckpointTag | null,
  points: readonly TrainingMetricPointView[],
): CheckpointLossJoin {
  if (tag === null || points.length === 0) {
    return { value: null, approximate: false };
  }
  let matched: TrainingMetricPointView | null = null;
  for (const point of points) {
    if (point.step <= tag.value && (matched === null || point.step > matched.step)) {
      matched = point;
    }
  }
  if (matched === null) {
    return { value: null, approximate: false };
  }
  const exact = matched.step === tag.value || matched.step === tag.value - 1;
  return { value: matched.value, approximate: !exact };
}

// 체크포인트 목록에 저장 시점 손실, best 판정, 평가 run 을 join 한 행을 만든다.
// best = 저장 시점 validation_loss 최소(같으면 먼저 저장된 쪽). 손실을 붙이지 못한 행은
// best 후보에서 빠진다.
export function buildCheckpointRows(
  policies: readonly SessionCheckpointView[],
  validationLossPoints: readonly TrainingMetricPointView[],
  summaries: readonly TrainingEvalRunSummaryView[],
): CheckpointAnalysisRow[] {
  const rows = policies.map((checkpoint) => {
    const tag = parseCheckpointTag(checkpoint.epoch);
    return {
      checkpoint,
      tag,
      loss: joinLossAtTag(tag, validationLossPoints),
      isBest: false,
      evalRuns: joinEvalRuns(tag, summaries),
    };
  });
  let best: CheckpointAnalysisRow | null = null;
  for (const row of rows) {
    if (row.loss.value !== null && (best === null || row.loss.value < (best.loss.value ?? 0))) {
      best = row;
    }
  }
  if (best !== null) {
    best.isBest = true;
  }
  return rows;
}

// 두 체크포인트 태그가 같은 체크포인트인지 판정한다. 단위와 번호가 모두 같아야 한다. Policy 이름
// 링크가 보낸 focus(정책의 epoch+budget_unit)를 표의 행(디렉터리 태그)과 맞출 때 쓴다. 어느
// 쪽이든 태그를 모르면(null) 같다고 판정하지 않는다.
export function isSameCheckpointTag(a: CheckpointTag | null, b: CheckpointTag | null): boolean {
  return a !== null && b !== null && a.unit === b.unit && a.value === b.value;
}

// 이 체크포인트를 평가한 run 들. 평가 요약의 epoch(정수)+budget_unit 을 태그와 맞춘다.
// epoch 를 모르는(null) run 은 어느 체크포인트에도 붙이지 않는다.
function joinEvalRuns(
  tag: CheckpointTag | null,
  summaries: readonly TrainingEvalRunSummaryView[],
): TrainingEvalRunSummaryView[] {
  if (tag === null) {
    return [];
  }
  return summaries.filter(
    (summary) => summary.epoch === tag.value && summary.budget_unit === tag.unit,
  );
}

// 시행(에피소드) 수로 읽는 지표 키. 벤치마크 이미지마다 이름이 달라 이 순서로 찾는다.
const TRIAL_COUNT_METRIC_KEYS = ["records", "requested_episodes", "total_episodes"] as const;

// metrics_all 의 유한 스칼라만 쓴다. 기록되지 않은 키와 NaN/Infinity 를 같은 "없음"으로 처리한다.
function metricNumber(
  metricsAll: Record<string, number> | null | undefined,
  key: string,
): number | null {
  const value = metricsAll?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function firstMetricNumber(
  metricsAll: Record<string, number> | null | undefined,
  keys: readonly string[],
): number | null {
  for (const key of keys) {
    const value = metricNumber(metricsAll, key);
    if (value !== null) {
      return value;
    }
  }
  return null;
}

export type SuccessRateWithCount = {
  // 성공률(0..1). 산출 전이면 null 이다.
  rate: number | null;
  // 시행(에피소드) 수. TRIAL_COUNT_METRIC_KEYS 순서로 찾는다.
  trials: number | null;
  // 성공 횟수. 기록된 total_true_success 를 먼저 쓰고, 없으면 rate×trials 를 반올림한다.
  successes: number | null;
};

// 성공률에 시행 횟수 n 을 함께 표시하기 위한 분해. n 없는 % 는 과신을 만든다.
export function successRateWithCount(
  metricsAll: Record<string, number> | null | undefined,
): SuccessRateWithCount {
  const rate = metricNumber(metricsAll, "success_rate");
  const trials = firstMetricNumber(metricsAll, TRIAL_COUNT_METRIC_KEYS);
  // 기록된 성공 횟수를 먼저 쓴다. rate×trials 반올림은 시행이 많을수록 실제와 차이가 난다.
  const recorded = metricNumber(metricsAll, "total_true_success");
  const successes =
    recorded ?? (rate !== null && trials !== null ? Math.round(rate * trials) : null);
  return { rate, trials, successes };
}

// 실패 에피소드 수. 실패 영상 부재 경고의 입력이다. 기록된 total_failure 가 있으면 그 값을 쓴다.
// 타임아웃은 실패와 별개 판정이다. 그래서 시행 수에서 성공 수를 빼면 타임아웃까지 실패로 세어
// 경고가 잘못 표시된다. total_failure 를 보내지 않는 이미지에서만 뺄셈으로 추정한다.
export function failureCount(metricsAll: Record<string, number> | null | undefined): number | null {
  const recorded = metricNumber(metricsAll, "total_failure");
  if (recorded !== null) {
    return recorded;
  }
  const { rate, trials, successes } = successRateWithCount(metricsAll);
  if (rate === null || trials === null || successes === null) {
    return null;
  }
  return Math.max(0, trials - successes);
}

export type EvalRunDurations = {
  // eval-run 이 차지한 전체 시간(ms). 파드 준비·체크포인트 다운로드·아티팩트 업로드를 포함한다.
  elapsedMs: number | null;
  // 평가기가 직접 측정한 순수 평가 시간(ms). 벤치마크 이미지가 wall_t 를 보내지 않으면 null 이다.
  computeMs: number | null;
};

// 평가에 걸린 시간의 두 축. training 의 `runtime_ms` 는 결과를 MLflow 에 기록한 구간만 측정한다.
// 그래서 실제 평가 시간의 1/1000 수준으로 나오므로 여기서 쓰지 않는다.
export function evalRunDurations(
  run: TrainingEvalMetricsView,
  summary: TrainingEvalRunSummaryView | null,
): EvalRunDurations {
  const elapsedSec =
    runDurationSeconds(run) ?? (summary === null ? null : runDurationSeconds(summary));
  const computeSec = metricNumber(run.metrics_all, "wall_t");
  return {
    elapsedMs: elapsedSec === null ? null : elapsedSec * 1000,
    computeMs: computeSec === null ? null : Math.round(computeSec * 1000),
  };
}

export type EvalOutcomeCounts = {
  success: number;
  failure: number;
  timeout: number;
  falsePositive: number;
};

// 에피소드 판정 내역. 성공률 0% 의 원인이 실패인지 타임아웃인지 카드에서 바로 읽게 한다.
// 네 지표가 하나도 없으면 null 이다(이 이미지는 판정을 보내지 않는다).
export function evalOutcomeCounts(
  metricsAll: Record<string, number> | null | undefined,
): EvalOutcomeCounts | null {
  const success = metricNumber(metricsAll, "total_true_success");
  const failure = metricNumber(metricsAll, "total_failure");
  const timeout = metricNumber(metricsAll, "total_env_timeout");
  const falsePositive = metricNumber(metricsAll, "total_false_positive");
  if (success === null && failure === null && timeout === null && falsePositive === null) {
    return null;
  }
  return {
    success: success ?? 0,
    failure: failure ?? 0,
    timeout: timeout ?? 0,
    falsePositive: falsePositive ?? 0,
  };
}

// 실패가 있었는데 실패 영상이 한 건도 없는 상태. 실패 영상은 필수 산출물이라 경고 대상이다.
export function hasMissingFailureVideos(
  failures: number | null,
  failureEpisodeCount: number,
): boolean {
  return failures !== null && failures > 0 && failureEpisodeCount === 0;
}
